// import { assertNotNull } from '@angular/compiler/src/output/output_ast';
import * as _ from 'lodash-es';
import * as ntt from 'number-to-words';
import {
    TogetherAuctionDIPEmploymentStatusType,
    TogetherAuctionDIPEntityType,
    TogetherAuctionDIPIncomeTypeBroad,
    TogetherAuctionDIPPaymentPeriod,
    TogetherAuctionDIPPOtherEmploymentStatusType,
    TogetherAuctionDIPNonOtherIncomeTypes,
    TogetherAuctionDIPOtherIncomeType,
} from './enums';


/* tslint:disable:variable-name */
/**
 * Class container for the Auction DIP Loan Details response.
 *
 * @export
 * @class TogetherAuctionDIPLoanDetails
 */
export class TogetherAuctionDIPLoanDetails {

    // Case properties.
    CaseId: string;
    LoanAmount: number;
    LoanTerm: number;
    PropertyValuation: number;
    PropertyType: string;
    DateLoanRequired: string;
    RepaymentStrategy: number;
    IsSecurity40PercentFamilyResident: boolean;
    IsDipJourney: boolean;
    SalesPerson: string;
    LeadReferenceNumber: string;
    IsOutSideCriteria: boolean;

    /**
     * Is this being saved?
     *
     * @returns
     * @memberof TogetherAuctionDIPLoanDetails
     */
    isDefaulted() {
        return this.CaseId === '00000000-0000-0000-0000-000000000000';
    }

    /**
     * Creates an instance of TogetherAuctionDIPLoanDetails.
     * @memberof TogetherAuctionDIPLoanDetails
     */
    constructor(params: object = {}) {
        params = Object.assign(this, _.defaults(params, {
            CaseId: '00000000-0000-0000-0000-000000000000',
            LoanTerm: 12,
            PropertyType: '0',
            DateLoanRequired: null,
            RepaymentStrategy: null,
            IsSecurity40PercentFamilyResident: false,
            IsDipJourney: true,
            SalesPerson: null,
            LeadReferenceNumber: null,
            IsOutSideCriteria: false
        }));
    }
};

/**
 * Common address as for some reason we have several types of address.
 *
 * @export
 * @class TogetherBaseAddress
 */
export class TogetherBaseAddress {
    HouseName: string;
    HouseNumber: string;
    Street1: string;
    Street2: string;
    District: string;
    PostTown: string;
    County: string;
    Postcode: string;
    UKCountryCodeId: number = 1;
    SubBuildingNumber: string;
    SubBuildingName: string;
    QASHouseName: string;
    /**
     * Gets address as string.
     *
     * @memberof TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses
     */
    getAddress() {
        return `${this.HouseName} ${this.HouseNumber} ${this.Street1} ${this.County} ${this.Postcode}`;
    }
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            HouseName: '',
            HouseNumber: '',
            Street1: '',
            Street2: null,
            District: null,
            PostTown: '',
            County: '',
            Postcode: '',
            UKCountryCodeId: 1,
            SubBuildingNumber: null,
            SubBuildingName: null,
            QASHouseName: null,
            CaseId: '00000000-0000-0000-0000-000000000000',
        }));
    }
}

/**
 * Parent address type.
 *
 * @export
 * @class TogetherAuctionDIPAddressParent
 * @extends {TogetherBaseAddress}
 */
export class TogetherAuctionDIPAddressParent extends TogetherBaseAddress {
    Id: number;
    CaseId: string;

    constructor(params: object = {}) {
        super();
        Object.assign(this, _.defaults(params, {
            Id: 0,
            HouseName: '',
            HouseNumber: '',
            Street1: '',
            Street2: null,
            District: null,
            PostTown: '',
            County: '',
            Postcode: '',
            UKCountryCodeId: 1,
            SubBuildingNumber: null,
            SubBuildingName: null,
            QASHouseName: null,
            CaseId: '00000000-0000-0000-0000-000000000000'
        }));
    }
}

/**
 * Addresses.
 *
 * @class TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses
 */
export class TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses extends TogetherBaseAddress {
    Id: string;
    CaseId: string;
    LegalBodyRoleId: string;
    AddressTypeId: number;
    AddressId: number;
    IsPreferred: false;
    EffectiveFromDate: string;
    ResidentialStatusId: number;
    Street: string;
    Country: string;
    TenancyDetail: string;
    IsRenting: boolean;
    Deleted: boolean;

    constructor(params: object = {}) {
        super();
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            LegalBodyRoleId: '00000000-0000-0000-0000-000000000000',
            HouseName: '',
            HouseNumber: '',
            Street1: '',
            Street2: '',
            District: '',
            PostTown: '',
            County: '',
            Postcode: '',
            UKCountryCodeId: 1,
            AddressTypeId: 2,
            AddressId: 0,
            SubBuildingNumber: null,
            SubBuildingName: null,
            QASHouseName: null,
            CaseId: '00000000-0000-0000-0000-000000000000',
            IsPreferred: false,
            EffectiveFromDate: null,
            TenancyDetail: null,
            ResidentialStatusId: 1,
            IsRenting: false,
            Deleted: false,
        }));
    }
}


/**
 * Legal body contacts for the applicant details.
 *
 * @class TogetherAuctionDIPApplicantDetailsLegalBodyContacts
 */
export class TogetherAuctionDIPApplicantDetailsLegalBodyContacts {
    Id: string;
    Communication: string;
    CommunicationType: number;
    /**
     * Communication types to be passed back.
     *
     * @memberof TogetherAuctionDIPApplicantDetailsLegalBodyContacts
     */

    /**
     * Setter for CommunicationType.
     *
     * @type {string}
     * @memberof TogetherAuctionDIPApplicantDetailsLegalBodyContacts
    * */
    set Type(newType: string) {
        var CommunicationTypes = this.getCommunicationTypes();
        this.CommunicationType = CommunicationTypes[newType];
    }

    getCommunicationTypes() {
        return {
            'None': 0,
            'HomeTelephone': 1,
            'Mobile': 2,
            'Email': 3,
            'Fax': 4,
            'WorkTelephone': 5
        };
    }

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            Type: 'None',
            Communication: ''
        }));

    }
}


/**
 * Request wrapper which contains all the API references only.
 *
 * @export
 * @class TogetherAuctionDIPApplicantDetail_Request
 */
export class TogetherAuctionDIPApplicantDetail_Request {
    ParentId: string;
    Id: string;
    CaseId: string;
    IsCompanyApplication: boolean;
    BrokerReference: string;
    CaseReference: string;
    lenderId: string;
    LegalBodyRoleApplicantId: string;
    LegalBodyRoleId: string;
    LegalBodyRoleTypeId: string;
    LegalBodyId: string;
    LegalBodyTypeId: string;
    Title: string; // How fitting it's defaulted as a woman - humankind evolved from her :)
    FirstName: string;
    MiddleName: string;
    Surname: string;
    DateOfBirth: string;
    BirthDateText: string; // New addition to support ISO date.
    MaritalStatus: string; // todo: check against contract
    NationalityId: string; // todo: check against contract
    Gender: string; // todo: check against contract
    CompanyName: string; // todo: check against contract
    IsJHGroupCompany: boolean;
    ApplicantSequence: number;
    PreviousName: string; // todo: check against contract
    ResidentCountryId: string; // todo: check against contract
    IsPersonalGarantor: false;
    HasUKPermanentRights: true;
    UKArrivalDate: number;
    PreferredContactPeriod: number; // todo: check against contract
    FirstLanguage: string; // todo: check against contract
    IsFluentInEnglish: true;
    RelationshipToMainApplicantId: string; // todo: check against contract
    IsVulnerable: false;
    IsVulnerableIndicatedByUnderwriter: false;
    VulnerabilityReason: string; // todo: check against contract
    AnticipatedRetirementAge: number;
    RegistrationNumber: string; // todo: check against contract
    IsBroker: false;
    BankAccountId: string; // todo: check against contract
    BankAccountRequest: string; // todo: check against contract
    DisburseToSolicitor: false; // todo: check against contract
    IsCBTL: false;
    NameIDVerificationDocumentTypeID: string; // todo: check against contract
    AddressIDVerificationDocumentTypeID: string; // todo: check against contract
    SignatureIDVerificationDocumentTypeID: string; // todo: check against contract
    IsSignatureCheckedAndAccepted: false;
    MarketingMarker: string; // todo: check against contract
    CompanyTypeId: string; // todo: check against contract
    IsGuarantorRequired: false;

    OkToContact: boolean = null;
    ApplicantMarketingPreference: ApplicantMarketingPreference = new ApplicantMarketingPreference();

    // Sub objects
    LegalBodyContacts: TogetherAuctionDIPApplicantDetailsLegalBodyContacts[] = [
        new TogetherAuctionDIPApplicantDetailsLegalBodyContacts({
            Id: '00000000-0000-0000-0000-000000000000',
            Type: 'Mobile',
            Communication: ''
        }),
        new TogetherAuctionDIPApplicantDetailsLegalBodyContacts({
            Id: '00000000-0000-0000-0000-000000000000',
            Type: 'Email',
            Communication: ''
        }),
    ];

    LegalBodyRoleAddresses: TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses[] = [
        new TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses(),
    ];

    MarketingPreferences: CaseApplicantMarketingPreferenceResponse[] = [
        new CaseApplicantMarketingPreferenceResponse(),
    ];

    get CaseApplicantMarketingPreferences() {
        return _.isNil(this.ApplicantMarketingPreference.CaseApplicantMarketingPreferences) ?
            null : this.ApplicantMarketingPreference.CaseApplicantMarketingPreferences;
    }

    set CaseApplicantMarketingPreferences(newVal: CaseApplicantMarketingPreferenceResponse[]) {
        this.MarketingPreferences = newVal;
    }

    // Getters and setters
    set EffectiveFromDate(newVal: Date) {

        this.LegalBodyRoleAddresses[0].EffectiveFromDate = newVal.toISOString();
    }

    /**
     * Getter alias for EffectiveFromDate
     *
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    get EffectiveFromDate() {
        // tslint:disable-next-line:max-line-length
        return _.isNil(this.LegalBodyRoleAddresses[0].EffectiveFromDate) ? null : new Date(this.LegalBodyRoleAddresses[0].EffectiveFromDate);
    }


    /**
     * Setter alias for HouseName
     *
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    set HouseName(newVal: string) {
        this.LegalBodyRoleAddresses[0].HouseName = newVal;
    }

    /**
     * Getter alias for HouseName
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    get HouseName() {
        return this.LegalBodyRoleAddresses[0].HouseName;
    }

    /**
     * Setter alias for HouseNumber
     *
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    set HouseNumber(newVal: string) {
        this.LegalBodyRoleAddresses[0].HouseNumber = newVal;
    }

    /**
    * Set the title as an integer.
    *
    * @memberof TogetherAuctionDIPApplicantDetail_Request
    */
    set _Title(newVal: string) {
        this.Title = newVal;
    }

    /**
     * Gets the title.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    get _Title() {
        return this.Title;
    }

    get _ApplicantNumber() {
        return _.startCase(ntt.toWords(this.ApplicantSequence));
    }

    /**
     * Sets Date of Birth as ISO string.
     *
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    set _DateOfBirth(newVal: Date) {
        this.BirthDateText = newVal.toDateString();
    }

    /**
     * Returns DOB as string.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    get _DateOfBirth() {
        var date = _.isNil(this.BirthDateText) ? this.DateOfBirth : this.BirthDateText;
        return _.isNil(date) == false ? new Date(date) : null;
    }

    /**
     * Getter function for full name.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    get FullName() {
        return this.getFullName();
    }

    /**
     * Gets the full name of the applicant. Not included in API request.
     *
     * @returns
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    getFullName() {
        var name: string = this.FirstName;
        name += _.isNil(this.MiddleName) ? name : ' ' + this.MiddleName;
        return this._Title + ' ' + name + ' ' + this.Surname;
    }

    /**
     * Tells if the full name is set.
     *
     * @returns
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    hasFullName() {
        return this.FirstName !== '' && this.Surname !== '';
    }

    /**
     * Creates an instance of TogetherAuctionDIPApplicantDetail_Request.
     * @param {any} param
     * @memberof TogetherAuctionDIPApplicantDetail_Request
     */
    constructor(param: object = {}) {
        Object.assign(this, _.defaults(param, {
            ParentId: '00000000-0000-0000-0000-000000000000',
            Id: '00000000-0000-0000-0000-000000000000',
            CaseId: '00000000-0000-0000-0000-000000000000',
            IsCompanyApplication: false,
            BrokerReference: null,
            CaseReference: null,
            lenderId: 428,
            LegalBodyRoleApplicantId: '00000000-0000-0000-0000-000000000000',
            LegalBodyRoleId: '00000000-0000-0000-0000-000000000000',
            LegalBodyRoleTypeId: 0,
            LegalBodyId: '00000000-0000-0000-0000-000000000000',
            LegalBodyTypeId: 0,
            Title: '',
            FirstName: '',
            MiddleName: '',
            Surname: '',
            DateOfBirth: null,
            BirthDateText: null,
            MaritalStatus: null,
            NationalityId: null,
            Gender: null,
            CompanyName: null,
            IsJHGroupCompany: false,
            ApplicantSequence: 0,
            PreviousName: null,
            ResidentCountryId: null,
            IsPersonalGuarantor: false,
            HasUKPermanentRights: null,
            DurationInYearsInUK: null,
            UKArrivalDate: null,
            PreferredContactPeriod: null,
            FirstLanguage: null,
            IsFluentInEnglish: null,
            RelationshipToMainApplicantId: null,
            IsVulnerable: null,
            IsVulnerableIndicatedByUnderWriter: null,
            VulnerabilityReason: null,
            AnticipatedRetirementAge: null,
            RegistrationNumber: null,
            IsBroker: false,
            BankAccountId: '00000000-0000-0000-0000-000000000000',
            BankAccountRequest: null,
            DisburseToSolicitor: false,
            IsCBTL: false,
            NameIDVerificationDocumentTypeID: null,
            AddressIDVerificationDocumentTypeID: null,
            SignatureIDVerificationDocumentTypeID: null,
            IsSignatureCheckedAndAccepted: null,
            MarketingMarker: null,
            CompanyTypeId: null,
            IsGuarantorRequired: null
        }));
    }
};

/**
 * Class container for applicant details.
 *
 * @export
 * @class CaseApplicantMarketingPreferenceResponse
 */
export class CaseApplicantMarketingPreferenceResponse {
    CaseApplicantId: string;
    CommunicationTypeId: number;
    ContactByEmail: boolean = false;
    ContactByPhone: boolean = false;
    ContactByPost: boolean = false;
    ContactByTextOrSMS: boolean = false;
    Id: string;
    MarketingPreferenceQuestionId: number;

    constructor(param: object = {}) {
        Object.assign(this, _.defaults(param, {
            CaseApplicantId: '00000000-0000-0000-0000-000000000000',
            Id: '00000000-0000-0000-0000-000000000000',
            CommunicationTypeId: 0,
            MarketingPreferenceQuestionId: 0,
            ContactByEmail: false,
            ContactByPhone: false,
            ContactByPost: false,
            ContactByTextOrSMS: false,
        }))
    }
};

/**
 * Class container for applicant details.
 *
 * @export
 * @class TogetherAuctionDIPApplicantDetails
 */
export class ApplicantMarketingPreference {
    ApplicantSequence: number;
    CaseApplicantMarketingPreferences: CaseApplicantMarketingPreferenceResponse[] = [];
    CaseId: string;
    Id: string;
    OkToContact: boolean;
    constructor(param: object = {}) {
        Object.assign(this, _.defaults(param, {
            Id: '00000000-0000-0000-0000-000000000000',
            CaseId: '00000000-0000-0000-0000-000000000000',
            ApplicantSequence: 0,
            OkToContact: false,
        }))
    }
};


/**
 * Class container for applicant details.
 *
 * @export
 * @class TogetherAuctionDIPApplicantDetails
 */
export class TogetherAuctionDIPApplicantDetails {

    /**
     * Pristine applicant API request to be sent to the API.
     *
     * @type {TogetherAuctionDIPApplicantDetail_Request}
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    Instance: TogetherAuctionDIPApplicantDetail_Request = new TogetherAuctionDIPApplicantDetail_Request();

    /**
     * Reference values
     */
    _lbc_types: Object = {
        'None': 0,
        'HomeTelephone': 1,
        'Mobile': 2,
        'Email': 3,
        'Fax': 4,
        'WorkTelephone': 5
    };

    /**
     * Helper variables.
     */
    __ui_birthdate_day: number;
    __ui_birthdate_month: number;
    __ui_birthdate_year: number;

    _uiPhone: boolean;
    _uiEmail: boolean;
    _uiPost: boolean;
    _uiSms: boolean;

    /**
     * E.164 format of phone number with +44 prefix.
     *
     * @type {string}
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    __ui_main_phone_number: string;

    /**
     * Used to display to the user the main phone number they entered.
     *
     * @type {string}
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    __ui_main_phone_number_display: string;
    __ui_email_address: string;
    __ui_postal_code: string;
    __ui_moved_in_date_year: number;
    __ui_moved_in_date_month: number;
    __ui_moved_in_date: Date;
    __ui_marketing_approval: boolean;

    /**
     * General utility functions.
     */

    /**
     * Converts a number into E164 format so it actually is usable.
     *
     * @param {any} phone_number
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    convertPhoneNumberToE164Format(phone_number: string) {
        var number = phone_number.replace(/([^\d+]|(?!^)\+)/gim, '');
        return number;
    }

    /**
     * Getters and setters.
     */

    /**
     * Getter for UI postal code.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_postal_code(newVal: string) {
        this.__ui_postal_code = newVal;
    }

    /**
     * Getter for UI postal code.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_postal_code() {
        var addr = _.isArray(this.Instance.LegalBodyRoleAddresses) && this.Instance.LegalBodyRoleAddresses.length > 0;
        return addr ? this.Instance.LegalBodyRoleAddresses[0].Postcode : '';
    }

    /**
     * Sets marketing approval;
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_marketing_approval(newVal: boolean) {
        this.__ui_marketing_approval = newVal;
    }

    /**
     * Gets value for marketing approval.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_marketing_approval() {
        return this.__ui_marketing_approval;
    }


    /**
     * Setter for address house name / number
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _house_name_number(newVal: string) {
        this.Instance.LegalBodyRoleAddresses[0].HouseName = newVal;
        this.Instance.LegalBodyRoleAddresses[0].HouseNumber = newVal;
    }


    set _house_street_name(newVal: string) {
        this.Instance.LegalBodyRoleAddresses[0].Street1 = newVal;
    }

    set _town(newVal: string) {
        this.Instance.LegalBodyRoleAddresses[0].PostTown = newVal;
    }

    set _city(newVal: string) {
        this.Instance.LegalBodyRoleAddresses[0].District = newVal;
    }

    set _county(newVal: string) {
        this.Instance.LegalBodyRoleAddresses[0].County = newVal;
    }

    set _postcode(newVal: string) {
        this.Instance.LegalBodyRoleAddresses[0].Postcode = newVal;
    }

    /**
     * Adds email address.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
        */
    addEmailAddress(email_address) {
        const email = this.Instance.LegalBodyContacts.find(y => y.CommunicationType == 3)
        email['Communication'] = email_address;
    }

    /**
     * Sets UI date for moved in month.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_moved_in_month(newVal: number) {

        // Sets moved in month
        this.__ui_moved_in_date_month = newVal;

        // Generate moved in date.
        this.generateMovedInDate();
    }

    /**
     * Setter for moved in year.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_moved_in_year(newVal: number) {

        // Sets moved in year
        this.__ui_moved_in_date_year = newVal;

        // Generate moved in date.
        this.generateMovedInDate();
    }

    /**
     * Get moved in month.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_moved_in_month() {
        var lbc = _.isArray(this.Instance.LegalBodyRoleAddresses) && this.Instance.LegalBodyRoleAddresses.length > 0;
        return lbc ? new Date(this.Instance.LegalBodyRoleAddresses[0].EffectiveFromDate).getMonth() : null;
    }

    /**
     * Get moved in year.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_moved_in_year() {
        return this.__ui_moved_in_date_year;
    }

    /**
     * Setter for the email address.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_email_address(newVal: string) {

        // Update the e-mail address in helper variable.
        this.__ui_email_address = newVal;

        // Add e-mail address to the legal body contacts object.
        this.addEmailAddress(this.__ui_email_address);
    }

    /**
     * Getter for the e-mail address.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_email_address() {
        var email = _.filter(this.Instance.LegalBodyContacts, (contact) => {
            return contact.CommunicationType == 3
        });
        return _.isArray(email) && email.length > 0 ? email[0].Communication : '';
    }

    /**
     * Add the main phone number as an object.
     *
     * @param {any} phone_number
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    addMainPhoneNumber(phone_number) {
        const substr = phone_number.substring(0, 1);
        const isMobile = phone_number.substring(0, 1) === '7' || phone_number.substring(0, 2) === '07' || phone_number.substring(0, 3) === '447' || phone_number.substring(0, 4) === '+447';
        const phone = this.Instance.LegalBodyContacts.find(y => y.CommunicationType == 1 || y.CommunicationType == 2)
        phone['CommunicationType'] = isMobile ? 2 : 1;
        phone['Communication'] = phone_number;
    }

    /**
     * Setter for phone number.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_phone_number(newVal: string) {
        // Convert the UI main phone number to E164 format so it's consistent.
        this.__ui_main_phone_number = this.convertPhoneNumberToE164Format(newVal);
        this.__ui_main_phone_number_display = newVal.replace(/[^0-9]/ig, '');

        // Add the number as an object.
        this.addMainPhoneNumber(this.__ui_main_phone_number);
    }

    /**
     * Getter for phone number property.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_phone_number() {
        const numbers = _.filter(this.Instance.LegalBodyContacts, (contact) => {
            return contact.CommunicationType === 2 || contact.CommunicationType === 1
        });
        return _.isArray(numbers) && numbers.length > 0 ? numbers[0].Communication : '';
    }

    /**
     * Sets the birthdate year (auto generates date)
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_birthdate_year(newVal: number) {
        this.__ui_birthdate_year = newVal;
        this.generateDateOfBirth();
    }

    /**
     * Sets the birthdate month (auto generates date)
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_birthdate_month(newVal: number) {
        this.__ui_birthdate_month = newVal;
        this.generateDateOfBirth();
    }

    /**
     * Sets the birthdate day (auto generates date)
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    set _ui_birthdate_day(newVal: number) {
        this.__ui_birthdate_day = newVal;
        this.generateDateOfBirth();
    }

    get uiPhone() {
        return this._uiPhone;
    }

    get uiEmail() {
        return this._uiEmail;
    }

    get uiPost() {
        return this._uiPost;
    }


    get uiSms() {
        return this._uiSms;
    }

    set uiPhone(newVal: boolean) {
        this._uiPhone = true;
        this.Instance.MarketingPreferences[0].ContactByPhone = newVal;
    }

    set uiPost(newVal: boolean) {
        this._uiPost = true;
        this.Instance.MarketingPreferences[0].ContactByPost = newVal;
    }
    set uiEmail(newVal: boolean) {
        this._uiEmail = true;
        this.Instance.MarketingPreferences[0].ContactByEmail = newVal;
    }
    set uiSms(newVal: boolean) {
        this._uiSms = true;
        this.Instance.MarketingPreferences[0].ContactByTextOrSMS = newVal;
    }
    /**
     * Gets property for birthdate day.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_birthdate_day() {
        return _.isNil(this.__ui_birthdate_day) ? null : this.__ui_birthdate_day;
    }

    /**
     * Gets property for birthdate month.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_birthdate_month() {
        return _.isNil(this.__ui_birthdate_month) ? null : this.__ui_birthdate_month;
    }

    /**
     * Gets property for birthdate year.
     *
     * @readonly
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    get _ui_birthdate_year() {
        return _.isNil(this.__ui_birthdate_year) ? null : this.__ui_birthdate_year;
    }

    // Helper functions

    /**
     * Generates the date of birth. Is done automatically as y/m/d items are entered.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    generateDateOfBirth() {
        if ((this instanceof Window) === false && _.isNil(this._ui_birthdate_year) === false) {
            this.Instance._DateOfBirth = new Date(this._ui_birthdate_year, this._ui_birthdate_month - 1, this._ui_birthdate_day);
        }
    }

    /**
     * Generates a moved in date.
     *
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    generateMovedInDate() {
        if ((this instanceof Window) == false && _.isNil(this._ui_moved_in_year) === false) {
            this.Instance.LegalBodyRoleAddresses[0].EffectiveFromDate =
                new Date(this._ui_moved_in_year, this._ui_moved_in_month - 1, 1).toISOString();
        }
    }

    /**
     * Outputs to string for API-ification.
     *
     * @returns
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    toString() {
        let obj = Object.assign({}, this);
        _.each(Object.keys(obj), (key) => {
            if (key.substr(0, 1) === '_') {
                delete obj[key];
            }
        });
        return JSON.stringify(obj);
    }

    /**
     * Removes a legal body contact by name from contacts.
     *
     * @param {any} legal_body_contact_name
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    removeLegalBodyContact(legal_body_contact_name) {
        const lbc = this._lbc_types[legal_body_contact_name];
        this.Instance.LegalBodyContacts = _.filter(this.Instance.LegalBodyContacts, (contact) => {
            return contact.CommunicationType !== lbc;
        });
    }


    /**
     * Creates an instance of TogetherAuctionDIPApplicantDetails.
     * @memberof TogetherAuctionDIPApplicantDetails
     */
    constructor(index = null, instance: object = null) {
        if (!_.isNil(index)) {
            this.Instance.ApplicantSequence = index;
        }
        if (!_.isNil(instance)) {
            Object.assign(this.Instance, instance);
        }
        if (_.isEmpty(this.Instance.LegalBodyRoleAddresses)) {
            this.Instance.LegalBodyRoleAddresses = [
                new TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses()
            ];
        }
    }

};


export class TogetherAuctionDIPSelfEmploymentDetails_LBRCompany {

    /**
     * Creates an instance of TogetherAuctionDIPSelfEmploymentDetails_LBRCompany.
     * @param {object} [params={}]
     * @memberof TogetherAuctionDIPSelfEmploymentDetails_LBRCompany
     */
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            AddressId: '0',
            AddressTypeId: '2',
            LegalBody: '00000000-0000-0000-0000-000000000000',
            Postcode: null
        }));
    }
}

export class TogetherAuctionDIPSelfEmploymentDetails_Incomes {

}

/**
 * Self employment details.
 *
 * @export
 * @class TogetherAuctionDIPSelfEmploymentDetails
 */
export class TogetherAuctionDIPSelfEmploymentDetails {
    Id: string;
    CAEId: string;
    SelfEmploymentStatusId: number;
    ShareHoldingPercentage: number;
    GrossIncomePA: number;
    NetIncomePA: number;
    NatureOfBusiness: string;
    Position: string;
    LBRTradingNameId: string;
    SelfEmploymentDate: string;
    TradingStartDate: string;
    TradingDuration: string;
    MembershipCompany: string;
    LimitedCompanyWorkTelephone: string;
    LimitedCompanyWorkTelephoneId: string;
    LimitedCompanyName: string;
    LimitedCompanyRegistrationNo: string;
    LBRCompany: TogetherAuctionDIPSelfEmploymentDetails_LBRCompany = new TogetherAuctionDIPSelfEmploymentDetails_LBRCompany();
    LBRCompanyCommunication: string;
    MethodOfPaymentId: string;
    IncomeEvidenceId: string;
    LimitedCompanyLegalBodyTypeId: string;
    LimitedCompanyLegalBodyRoleTypeId: string;
    LegalBodyId: string;
    SelfEmploymentIncomes: TogetherAuctionDIPSelfEmploymentDetails_Incomes[] = [];
    EmploymentStatusId: string;
    PaymentPeriodTypeId: number;
    OtherSecuredMonthlyDebtRepayment: string;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CAEId: null,
            SelfEmploymentStatusId: 2,
            ShareHoldingPercentage: null,
            GrossIncomePA: 0,
            NetIncomePA: null,
            NatureOfBusiness: null,
            Position: null,
            LBRTradingNameId: null,
            SelfEmploymentDate: null,
            TradingStartDate: null,
            TradingDuration: null,
            MembershipCompany: null,
            LimitedCompanyWorkTelephone: '',
            LimitedCompanyWorkTelephoneId: '00000000-0000-0000-0000-000000000000',
            LimitedCompanyName: '',
            LimitedCompanyRegistrationNo: null,
            LBRCompanyCommunication: null,
            LimitedCompanyLegalBodyRoleTypeId: null,
            LimitedCompanyLegalBodyTypeId: null,
            MethodOfPaymentId: null,
            OtherSecuredMonthlyDebtRepayment: null,
            EmploymentStatusId: '3',
            LegalBodyId: null,
            IncomeEvidenceId: null
        }));
    }
} 3


export class TogetherAuctionDIPOtherEmploymentDetails_Incomes {
    Id: string;
    CAEId: string;
    OtherIncomeTypeId: number;
    Amount: number;
    Description: string;

    constructor(params: object = {}) {
        Object.assign(_.defaults(params, {
            Id: 0,
            CAEId: '00000000-0000-0000-0000-000000000000',
            OtherIncomeTypeId: 1,
            Amount: 0,
            Description: 'Other'
        }));
    }
}


export class TogetherAuctionDIPOtherEmploymentDetails {
    CAEId: string = '00000000-0000-0000-0000-000000000000';
    OtherNote: number;
    OtherEmploymentStatusType: number;
    CaseApplicantId: string;
    PaymentPeriodTypeId: number;
    IncomeEvidenceId: string;
    RentalIncome: number;
    InvestmentIncome: number;
    MaintenancebyCourtOrder: null;
    OtherSecuredMonthlyDebtRepayment: null;
    OtherIncomes: Array<object> = [];

    /**
     * Because awful design.
     *
     * @private
     * @type {Map<TogetherAuctionDIPIncomeTypeBroad,string>}
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    private get OtherIncomeNames(): Map<TogetherAuctionDIPIncomeTypeBroad, string> {
        return new Map<TogetherAuctionDIPIncomeTypeBroad, string>([
            [TogetherAuctionDIPIncomeTypeBroad.Investments, 'Investment income'],
            [TogetherAuctionDIPIncomeTypeBroad.RentalIncome, 'Gross Rental Income'],
        ]);
    }


    /**
     * Income types which have other types, and their link to the broad income types.
     *
     * @private
     * @type {Map<TogetherAuctionDIPIncomeTypeBroad,TogetherAuctionDIPPOtherEmploymentStatusType>}
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    private get OtherIncomeTypes(): Map<TogetherAuctionDIPIncomeTypeBroad, TogetherAuctionDIPPOtherEmploymentStatusType> {
        return new Map<TogetherAuctionDIPIncomeTypeBroad, TogetherAuctionDIPPOtherEmploymentStatusType>([
            [TogetherAuctionDIPIncomeTypeBroad.Investments, TogetherAuctionDIPPOtherEmploymentStatusType.InvestmentIncome],
            [TogetherAuctionDIPIncomeTypeBroad.RentalIncome, TogetherAuctionDIPPOtherEmploymentStatusType.GrossRentalIncome],
        ]);
    }

    /**
     * Used to determine if the OtherIncomes section is populated or not.
     *
     * @private
     * @type {Array<TogetherAuctionDIPIncomeTypeBroad>}
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    private get nonOtherIncomes(): Array<TogetherAuctionDIPIncomeTypeBroad> {
        return [
            TogetherAuctionDIPIncomeTypeBroad.Pension,
            TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits,
            TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed
        ];
    }


    /**
     * Map which links the non other income types to the broad income types.
     *
     * @private
     * @type {Map<number,number>}
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    private get NonOtherIncomeTypes(): Map<TogetherAuctionDIPIncomeTypeBroad, TogetherAuctionDIPNonOtherIncomeTypes> {
        return new Map<TogetherAuctionDIPIncomeTypeBroad, TogetherAuctionDIPNonOtherIncomeTypes>([
            [TogetherAuctionDIPIncomeTypeBroad.Pension, TogetherAuctionDIPNonOtherIncomeTypes.Retirement],
            [TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits, TogetherAuctionDIPNonOtherIncomeTypes.InReceiptOfStateBenefits],
            [TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed, TogetherAuctionDIPNonOtherIncomeTypes.Unemployment]
        ]);
    }

    /**
     * Gets the other income type.
     *
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    getOtherIncomeType(type: TogetherAuctionDIPIncomeTypeBroad): TogetherAuctionDIPOtherIncomeType {
        return this.nonOtherIncomes.indexOf(type) >= 0 ? TogetherAuctionDIPOtherIncomeType.NonOther : TogetherAuctionDIPOtherIncomeType.Other;
    }


    /**
     * Gets the existing other income.
     *
     * @returns
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    getExistingOtherIncome() {
        if (!_.isEmpty(this.OtherIncomes)) {
            return this.OtherIncomes[0];
        }
    }

    /**
     * Sets the other income array item .
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} type
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    setOtherIncomeArrayItem(type: TogetherAuctionDIPIncomeTypeBroad) {

        // Get the name
        const name = this.OtherIncomeNames.get(type);

        // Get the Id.
        const id = this.OtherIncomeTypes.get(type);

        // Get the other income.
        const otherIncome = this.getExistingOtherIncome();

        // Get CAEId
        const CAEId = this.CAEId;

        // Get the id
        const Id = !_.isEmpty(otherIncome) ? otherIncome['Id'] : 0;

        // Other incomes.
        this.OtherIncomes = [{
            Id: Id,
            CAEId: CAEId, // Betcha this screws up.
            OtherIncomeTypeId: id,
            Amount: null,
            Description: name
        }];
    }

    /**
     * Sets the income type and determines which parts of the
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} type
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    setIncomeType(type: TogetherAuctionDIPIncomeTypeBroad) {
        const thisType = this.getOtherIncomeType(type);
        if (thisType === TogetherAuctionDIPOtherIncomeType.Other) {

            // Reset the income types so the array isn't present.
            this.reset(true);

            // Now prepare the other array.
            this.setOtherIncomeArrayItem(type);

            // Now we need to always set as 7 because, amazing design.
            this.OtherEmploymentStatusType = 7;

        } else {

            // Reset the array again.
            this.reset();

            // Now we need to prepare the OtherEmploymentStatusType here.
            this.OtherEmploymentStatusType = this.NonOtherIncomeTypes.get(type);
        }
    }

    /**
     * Reset the Other Incomes array.
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} type
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */


    reset(isOther: boolean = false) {

        // If it's other, and other is already set, do nothing.
        if (isOther && !_.isEmpty(this.OtherIncomes)) {
            return;
        }

        // Otherwise, reset the otherIncomes to empty.
        this.OtherIncomes = [];
    }


    /**
     * Sets the income on this object.
     *
     * @param {number} income
     * @param {TogetherAuctionDIPIncomeTypeBroad} type
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    setIncome(income: number, type: TogetherAuctionDIPIncomeTypeBroad) {
        const thisType = this.getOtherIncomeType(type);

        // Init all the arrays and stuff.
        this.setIncomeType(type);

        // If income type is other, we need to set it on the OtherIncomes array.
        if (thisType === TogetherAuctionDIPOtherIncomeType.Other) {

            // Take the income item
            const thisIncome = this.OtherIncomes[0];

            // Set the amount property in it.
            thisIncome['Amount'] = income;

            // Then re-set in the income.
            this.OtherIncomes = [thisIncome];
        } else {

            // Otherwise set it on the other employment type (yeah, makes no sense to me either.. )
            this.OtherNote = income;
        }
    }


    /**
     * Gets an income.
     *
     * @param {TogetherAuctionDIPPOtherEmploymentStatusType} type
     * @returns
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    getIncome(type: TogetherAuctionDIPIncomeTypeBroad) {

        // Get the type
        const thisType = this.getOtherIncomeType(type);

        // For those with other types, get from the array, otherwise get from OtherNote.
        if (thisType === TogetherAuctionDIPOtherIncomeType.Other) {
            const thisIncome = this.OtherIncomes[0];
            return thisIncome['Amount'];
        } else {
            return this.OtherNote;
        }
    }

    /**
     * Gets the income type based upon the
     *
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    getIncomeType() {
        const income: Array<number> = _.transform(this.OtherIncomes, (iter, n) => {
            const incomeVal = n['Amount'];
            if (!_.isNil(incomeVal)) {
                iter.push(n['OtherIncomeTypeId']);
            }
            return true;
        }, []);
        return _.isEmpty(income) ? TogetherAuctionDIPPOtherEmploymentStatusType.Other : income[0];
    }


    /**
     * Creates an instance of TogetherAuctionDIPOtherEmploymentDetails.
     * @param {object} [params={}]
     * @memberof TogetherAuctionDIPOtherEmploymentDetails
     */
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            CAEId: '00000000-0000-0000-0000-000000000000',
            OtherNote: null,
            OtherEmploymentStatusType: 7,
            CaseApplicantId: '00000000-0000-0000-0000-000000000000',
            PaymentPeriodTypeId: null,
            IncomeEvidenceId: null,
            RentalIncome: null,
            InvestmentIncome: null,
            MaintenancebyCourtOrder: null,
            OtherSecuredMonthlyDebtRepayment: null,
        }))
    }
}

export class TogetherAuctionDIPEmploymentDetails {
    Id: string;
    CAEId: string;
    NatureOfBusiness: string;
    Position: string;
    PositiongOfContact: string;
    MethodOfPaymentId: string;
    IsPositionPayment: boolean;
    TemporaryPositionDetail: string;
    LBREmployerId: string;
    Contact: string;
    EmployerDuration: string;
    Employer: string;
    AverageContractualHours: number;
    GrossIncomePA: number;
    NetIncomePM: number = 0;
    MonthlyBonusCommission: number;
    LegalBodyRoleAddresses: TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses;
    LegalBodyContacts: TogetherAuctionDIPApplicantDetailsLegalBodyContacts;
    IncomeEvidenceTypeId: string;
    EmploymentIncomeEvidenceId: string;
    LegalBodyId: string;
    EmploymentStatusId: string;
    WorkTelephoneNumber: string;
    LegalbodyCommunicationId: string;
    TelephoneNumbeofContact: string;
    PaymentPeriodTypeId: number;
    TotalNetAnnualIncome: number;
    OtherSecuredMonthlyDebtRepayment: 0;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CAEId: '00000000-0000-0000-0000-000000000000',
            NatureOfBusiness: '',
            Position: '',
            PositionOfContact: null,
            MethodOfPaymentId: null,
            IsPositionPermanent: null,
            TemporaryPositionDetail: null,
            LBREmployerId: '00000000-0000-0000-0000-000000000000',
            Contact: null,
            EmploymentDuration: 'null',
            Employer: null,
            AverageContractualHours: null,
            GrossIncomePA: null,
            NetIncomePM: 0,
            MonthlyBonusCommission: null,
            LegalBodyContacts: null,
            IncomeEvidenceTypeId: null,
            EmploymentIncomeEvidenceId: '00000000-0000-0000-0000-000000000000',
            LegalBodyId: '00000000-0000-0000-0000-000000000000',
            EmploymentStatusId: '1',
            WorkTelephoneNumber: null,
            LegalbodyCommunicationId: '00000000-0000-0000-0000-000000000000',
            TelephoneNumbeofContact: null,
            PaymentPeriodTypeId: null,
            TotalNetAnnualIncome: null,
            OtherSecuredMonthlyDebtRepayment: 0,
            LegalBodyRoleAddresses: new TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses()
        }))
    }
}

export class TogetherAuctionDIPIncomeDetails_EmploymentStatus {
    Id: number;
    Description: string;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: 1,
            Description: 'Full Time Employed'
        }));
    }
}

export class TogetherAuctionDIPIncomeDetails_PaymentPeriodType {
    Id: number;
    Description: string;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: 1,
            Description: 'Weekly'
        }));
    }
}



/**
 * Main income class .
 *
 * @export
 * @class TogetherAuctionDIPIncomeDetails
 */
export class TogetherAuctionDIPIncomeDetails {
    Id: string;
    AplicationProcess: string;
    CaseId: string;
    CaseApplicantId: string;
    EmploymentStatusId: string;
    PaymentPeriodTypeId: string;
    IncomeEvidenceId: string;
    SelfEmploymentDetails: TogetherAuctionDIPSelfEmploymentDetails[] = [];
    OtherEmploymentTypes: TogetherAuctionDIPOtherEmploymentDetails[] = [];
    EmploymentDetails: TogetherAuctionDIPEmploymentDetails[] = [];
    IsCBTL: boolean;
    IsBroker: boolean;
    EmploymentStatus: TogetherAuctionDIPIncomeDetails_EmploymentStatus[];
    PaymentPeriodType: TogetherAuctionDIPIncomeDetails_PaymentPeriodType[];
    TotalGrossIncome: number; // Response only.
    WorkTelephone: number; // Response only.


    /**
     * Resets the arrays so that they don't contain old data.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    resetEmploymentArrays() {
        this.EmploymentDetails = [];
        this.SelfEmploymentDetails = [];
        this.OtherEmploymentTypes = [];
    }

    /**
     * Create the arrays.
     *
     * @param {TogetherAuctionDIPEmploymentStatusType} type
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    initEmploymentArray(type: TogetherAuctionDIPEmploymentStatusType) {
        if (type === TogetherAuctionDIPEmploymentStatusType.FullTime ||
            type === TogetherAuctionDIPEmploymentStatusType.PartTime) {
            this.EmploymentDetails = [
                new TogetherAuctionDIPEmploymentDetails()
            ];
        } else if (type === TogetherAuctionDIPEmploymentStatusType.SelfEmployed) {
            this.SelfEmploymentDetails = [
                new TogetherAuctionDIPSelfEmploymentDetails()
            ];
        } else {
            this.OtherEmploymentTypes = [
                new TogetherAuctionDIPOtherEmploymentDetails()
            ];
        }
    }

    /**
     * Gets the delete ID for the income.
     *
     * @param {TogetherAuctionDIPEmploymentStatusType} type
     * @returns
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    getDeleteId(type: TogetherAuctionDIPEmploymentStatusType) {
        if (type === TogetherAuctionDIPEmploymentStatusType.FullTime ||
            type === TogetherAuctionDIPEmploymentStatusType.PartTime) {
            return this.EmploymentDetails[0].Id;
        } else if (type === TogetherAuctionDIPEmploymentStatusType.SelfEmployed) {
            return this.SelfEmploymentDetails[0].Id;
        } else {
            return this.OtherEmploymentTypes[0].CAEId;
        }
    }


    /**
     * Checks if this is empty.
     *
     * @readonly
     * @type {boolean}
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    get isNew(): boolean {
        return _.isEmpty(this.SelfEmploymentDetails) === true &&
            _.isEmpty(this.EmploymentDetails) === true &&
            _.isEmpty(this.OtherEmploymentTypes) === true;
    }

    /**
     * Getter for company name.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    get companyName() {
        if (_.isEmpty(this.SelfEmploymentDetails)) {
            return '';
        }
        return this.SelfEmploymentDetails[0].LimitedCompanyName;
    }


    /**
     * Setter for company name.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    set companyName(name: string) {
        if (_.isEmpty(this.SelfEmploymentDetails)) {
            return;
        }
        this.SelfEmploymentDetails[0].LimitedCompanyName = name;
    }


    /**
     * Getter for self employment status.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    get selfEmploymentStatus() {
        if (_.isEmpty(this.SelfEmploymentDetails)) {
            return 1;
        }
        return _.toInteger(this.SelfEmploymentDetails[0].SelfEmploymentStatusId);
    }

    /**
     * Setter for self employment status.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    set selfEmploymentStatus(status: number) {
        if (_.isEmpty(this.SelfEmploymentDetails)) {
            return;
        }
        this.SelfEmploymentDetails[0].SelfEmploymentStatusId =
            status === null ? null : _.toInteger(status);
    }

    /**
     * Sets the payment period type ID.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    set paymentPeriodTypeId(id: TogetherAuctionDIPPaymentPeriod) {
        this.PaymentPeriodTypeId = _.toString(id);
    }


    /**
     * Setter for employment status, which sets and resets the arrays.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    set employmentStatus(status: TogetherAuctionDIPIncomeTypeBroad) {

        // Statuses in the UI aren't the same as the API statuses.. So, we have to bridge the gap.
        const vals = new Map<number, number>([
            [TogetherAuctionDIPIncomeTypeBroad.FullTime, TogetherAuctionDIPEmploymentStatusType.FullTime],
            [TogetherAuctionDIPIncomeTypeBroad.PartTime, TogetherAuctionDIPEmploymentStatusType.PartTime],
            [TogetherAuctionDIPIncomeTypeBroad.SelfEmployed, TogetherAuctionDIPEmploymentStatusType.SelfEmployed],
            [TogetherAuctionDIPIncomeTypeBroad.Pension, TogetherAuctionDIPEmploymentStatusType.Retirement],
            [TogetherAuctionDIPIncomeTypeBroad.Investments, TogetherAuctionDIPEmploymentStatusType.Other],
            [TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits, TogetherAuctionDIPEmploymentStatusType.InReceiptOfBenefits],
            [TogetherAuctionDIPIncomeTypeBroad.RentalIncome, TogetherAuctionDIPEmploymentStatusType.Other],
            [TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed, TogetherAuctionDIPEmploymentStatusType.Unemployment]
        ]);

        // We get the status that is linked to the broad type used on the UI and then set the Id as required.
        const equivalentStatus = vals.get(status);

        if (!_.isNil(equivalentStatus) && _.toString(this.EmploymentStatusId) !== _.toString(equivalentStatus)) {

            // Set the employment status Id
            this.EmploymentStatusId = _.toString(vals.get(equivalentStatus));

            // Reset any of the other arrays, regardless of if they have contents.
            this.resetEmploymentArrays();

            // These use the same object.
            this.initEmploymentArray(equivalentStatus);

            // Set any sub object stuff.
            this.setSubElementStatus(status);

            // Reset any sub elements, because, why not have 30 incomes in an array? <3
            this.resetSubElements();
        }
    }

    set caseApplicantId(id: string) {
        const sId = _.toInteger(this.EmploymentStatusId);
        this.CaseApplicantId = id;
        if (sId === TogetherAuctionDIPEmploymentStatusType.FullTime ||
            sId === TogetherAuctionDIPEmploymentStatusType.PartTime ||
            sId === TogetherAuctionDIPEmploymentStatusType.SelfEmployed) {
            return; // Doesn't have one. Consistency is amazing, isn't it?
        } else if (sId === TogetherAuctionDIPEmploymentStatusType.InReceiptOfBenefits ||
            sId === TogetherAuctionDIPEmploymentStatusType.Retirement ||
            sId === TogetherAuctionDIPEmploymentStatusType.InReceiptOfBenefits ||
            sId === TogetherAuctionDIPEmploymentStatusType.Unemployment
        ) {
            this.OtherEmploymentTypes[0].CaseApplicantId = id; // Dat consistency.
        } else {
            return;
        }
    }

    get employmentStatusId() {
        const statusId = _.toString(this.EmploymentStatusId);

        if (statusId === '7') {
            const statusNum = _.toInteger(statusId);
            const otherIncomeType = this.OtherEmploymentTypes[0].getIncomeType();
            return this.getSubStatus(otherIncomeType) || TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed;
        } else {
            return statusId;
        }
    }


    /**
     * Getter used to bridge between string and int on employment status.
     *
     * @readonly
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    get employmentStatus() {
        return _.toInteger(this.EmploymentStatusId);
    }

    /**
     * Resets any of the sub statuses (other income types) if switched.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    resetSubElements() {
        if (!_.isEmpty(this.OtherEmploymentTypes)) {
            this.OtherEmploymentTypes[0].reset();
        }
    }

    getSubStatus(statusNum: number) {
        const subStatuses = new Map<number, number>([
            [TogetherAuctionDIPPOtherEmploymentStatusType.PrivatePension, TogetherAuctionDIPIncomeTypeBroad.Pension],
            [TogetherAuctionDIPPOtherEmploymentStatusType.InvestmentIncome, TogetherAuctionDIPIncomeTypeBroad.Investments],
            [TogetherAuctionDIPPOtherEmploymentStatusType.DSSPayments, TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits],
            [TogetherAuctionDIPPOtherEmploymentStatusType.GrossRentalIncome, TogetherAuctionDIPIncomeTypeBroad.RentalIncome],
            [TogetherAuctionDIPPOtherEmploymentStatusType.Other, TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed]
        ]);
        return subStatuses.get(statusNum);
    }


    /**
     * Set the payment period type Id.
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} status
     * @param {number} typeId
     * @returns
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    setPaymentPeriodTypeId(status: TogetherAuctionDIPIncomeTypeBroad, typeId: number) {
        if (status === TogetherAuctionDIPIncomeTypeBroad.Init) {
            return;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.FullTime ||
            status === TogetherAuctionDIPIncomeTypeBroad.PartTime) {
            this.EmploymentDetails[0].PaymentPeriodTypeId = typeId;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.SelfEmployed) {
            this.SelfEmploymentDetails[0].PaymentPeriodTypeId = typeId;
            this.IncomeEvidenceId = null;
        } else {
            this.OtherEmploymentTypes[0].PaymentPeriodTypeId = null;
        }
    }

    /**
     * Sets the status on a sub-element (excluding other) - due to uncommunicated API requirements.
     *
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    setSubElementStatus(status: TogetherAuctionDIPIncomeTypeBroad) {
        if (status === TogetherAuctionDIPIncomeTypeBroad.FullTime) {
            this.EmploymentDetails[0].EmploymentStatusId = '1';
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.PartTime) {
            this.EmploymentDetails[0].EmploymentStatusId = '2';
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.SelfEmployed) {
            this.SelfEmploymentDetails[0].EmploymentStatusId = '3';
        } else {
            this.OtherEmploymentTypes[0].setIncomeType(status);
        }
    }


    /**
     * Used for setting statuses on other income types.
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} status
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    setSubStatus(income: number, status: TogetherAuctionDIPIncomeTypeBroad) {

        // Because our API is so needlessly complicated, the other incomes need to be set with different status codes..
        const subStatuses = new Map<number, number>([
            [TogetherAuctionDIPIncomeTypeBroad.Pension, TogetherAuctionDIPPOtherEmploymentStatusType.PrivatePension],
            [TogetherAuctionDIPIncomeTypeBroad.Investments, TogetherAuctionDIPPOtherEmploymentStatusType.InvestmentIncome],
            [TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits, TogetherAuctionDIPPOtherEmploymentStatusType.DSSPayments],
            [TogetherAuctionDIPIncomeTypeBroad.RentalIncome, TogetherAuctionDIPPOtherEmploymentStatusType.GrossRentalIncome],
            [TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed, TogetherAuctionDIPPOtherEmploymentStatusType.Other]
        ]);

        const elem = subStatuses.get(status);

        // If it's not, welp, go away, otherwise, set in other income types.
        if (!_.isNil(elem)) {
            this.OtherEmploymentTypes[0].setIncome(income, status);
        }
    }

    /**
     * Sets the income based upon the broad type used on UI.
     *
     * @param {number} income
     * @param {TogetherAuctionDIPIncomeTypeBroad} status
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    setIncome(income: number, status: TogetherAuctionDIPIncomeTypeBroad, otherStatus: TogetherAuctionDIPPOtherEmploymentStatusType = null) {
        if (status === TogetherAuctionDIPIncomeTypeBroad.Init) {
            return;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.FullTime ||
            status === TogetherAuctionDIPIncomeTypeBroad.PartTime) {
            this.EmploymentDetails[0].NetIncomePM = income;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.SelfEmployed) {
            this.SelfEmploymentDetails[0].NetIncomePA = income;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.RentalIncome ||
            status === TogetherAuctionDIPIncomeTypeBroad.Investments ||
            status === TogetherAuctionDIPIncomeTypeBroad.Pension ||
            status === TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed ||
            status === TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits) {
            this.OtherEmploymentTypes[0].setIncome(income, status);
        } else {
            return; // Should be implicit now.
        }
    }

    /**
     * Gets the income.
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} status
     * @returns
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    getIncome(status: TogetherAuctionDIPIncomeTypeBroad) {
        if (status === TogetherAuctionDIPIncomeTypeBroad.Init) {
            return;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.FullTime ||
            status === TogetherAuctionDIPIncomeTypeBroad.PartTime) {
            return this.EmploymentDetails[0].NetIncomePM;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.SelfEmployed) {
            return this.SelfEmploymentDetails[0].NetIncomePA;
        } else if (status === TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed) {
            return 0;
        } else if (
            status === TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits ||
            status === TogetherAuctionDIPIncomeTypeBroad.Investments ||
            status === TogetherAuctionDIPIncomeTypeBroad.Pension ||
            status === TogetherAuctionDIPIncomeTypeBroad.RentalIncome
        ) {
            return this.OtherEmploymentTypes[0].getIncome(status);
        } else {
            return 0;
        }
    }

    /**
     * Get the display title for the income.
     *
     * @param {TogetherAuctionDIPIncomeTypeBroad} status
     * @returns
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    getIncomeDisplay(status: TogetherAuctionDIPEntityType) {

        const broadStatus = _.toInteger(this.employmentStatusId);

        const employmentDisplayTypes: Map<number, string> =
            new Map<number, string>([
                [TogetherAuctionDIPIncomeTypeBroad.Init, 'Please select an income type'],
                [TogetherAuctionDIPIncomeTypeBroad.FullTime, 'Full-time employment income'],
                [TogetherAuctionDIPIncomeTypeBroad.PartTime, 'Part-time employment income'],
                [TogetherAuctionDIPIncomeTypeBroad.InReceiptOfStateBenefits, 'State benefit income'],
                [TogetherAuctionDIPIncomeTypeBroad.Investments, 'Investment income'],
                [TogetherAuctionDIPIncomeTypeBroad.Pension, 'Pension income'],
                [TogetherAuctionDIPIncomeTypeBroad.RentalIncome, 'Rental income'],
                [TogetherAuctionDIPIncomeTypeBroad.SelfEmployed, 'Self-employed income'],
                [TogetherAuctionDIPIncomeTypeBroad.ImCurrentlyUnemployed, 'Unemployed']
            ]);
        return employmentDisplayTypes.get(broadStatus);
    }


    /**
     * Creates an instance of TogetherAuctionDIPIncomeDetails.
     * @param {object} [params={}]
     * @memberof TogetherAuctionDIPIncomeDetails
     */
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CaseId: '00000000-0000-0000-0000-000000000000',
            CaseApplicantId: '00000000-0000-0000-0000-000000000000',
            IsCBTL: false,
            IsBroker: false
        }));

        const employmentDetails: Array<object> = params['EmploymentDetails'] || [];
        const selfEmploymentDetails: Array<object> = params['SelfEmploymentDetails'] || [];
        const otherEmploymentDetails: Array<object> = params['OtherEmploymentTypes'] || [];

        this.EmploymentDetails = employmentDetails.map(details => new TogetherAuctionDIPEmploymentDetails(details));
        this.SelfEmploymentDetails = selfEmploymentDetails.map(details => new TogetherAuctionDIPSelfEmploymentDetails(details));
        this.OtherEmploymentTypes = otherEmploymentDetails.map(details => new TogetherAuctionDIPOtherEmploymentDetails(details));
    }
}


export class TogetherAuctionDIPSecurityRequest {

    Id: string;
    AddressId: number;
    Deleted: boolean;
    AddressRequest: TogetherAuctionDIPAddressParent = new TogetherAuctionDIPAddressParent();

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            AddressId: 0,
            Deleted: false,
        }));
    }
}


export class TogetherAuctionDIPCaseSecurityAttributeRequest {

    Id: string;
    CaseSecurityId: string;
    PropertyCategoryId: string;
    UpdatedPropertyCategoryId: string;
    PropertyTypeId: number;
    PropertyDetailId: string;
    ConstructionId: number;
    OtherPropertyType: string;
    OtherPropertyDetail: string;
    OtherConstruction: string;
    YearBuilt: number;
    Stories: number;
    Bedroom: number;
    Reception: number;
    Garage: number;
    Bathroom: number;
    Kitchen: number;
    IsParking: boolean;
    FloorArea: number;
    FloorNumber: number;
    AcresOfLand: number;
    Deleted: boolean = false;
    PriorChargeLender: string;
    PriorChargeDate: string;
    ExLocalAuthority: boolean;
    Toilets: number;
    Basement: boolean;
    NewDwellingId: number = 0;
    HouseInMultipleOccupation: boolean;
    IntendedBedrooms: number;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: 0,
            CaseSecurityId: '00000000-0000-0000-0000-000000000000',
            PropertyCategoryId: null,
            UpdatedPropertyCategoryId: null,
            PropertyTypeId: null,
            PropertyDetailId: null,
            ConstructionId: null,
            OtherPropertyType: null,
            OtherPropertyDetail: null,
            OtherConstruction: null,
            YearBuilt: null,
            Stories: null,
            Bedroom: null,
            Reception: null,
            Garage: null,
            Bathroom: null,
            Kitchen: null,
            IsParking: null,
            FloorArea: null,
            FloorNumber: null,
            AcresOfLand: null,
            Deleted: false,
            PriorChargeLender: null,
            PriorChargeDate: null,
            ExLocalAuthority: null,
            Toilets: null,
            Basement: null,
            NewDwellingId: 0,
            HouseInMultipleOccupation: null,
            IntendedBedrooms: null
        }))
    }

}

export class TogetherAuctionDIPValuationRequest {

    Id: string;
    CaseSecurityId: string;
    ValuationMethodId: string;
    ValuationCompany: string;
    ValuationDate: string;
    OpenMarketValue: number;
    ProjectedMarketValue: number;
    PurchasePrice: number;
    EstimatedValue: number;
    Note: string;
    Deleted: boolean;
    ValuationTypeId: string;
    ValuationAmount: number;
    IsInLTV: boolean;
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CaseSecurityId: '00000000-0000-0000-0000-000000000000',
            ValuationMethodId: null,
            ValuationCompany: null,
            ValuationDate: null,
            OpenMarketValue: null,
            ProjectedMarketValue: null,
            PurchasePrice: null,
            EstimatedValue: null,
            Note: null,
            Deleted: false,
            ValuationTypeId: null,
            ValuationAmount: null,
            IsInLTV: null
        }))
    }
}

/**
 * Existing charge
 *
 * @export
 * @class TogetherAuctionDIPExistingChargeDetail
 */
export class TogetherAuctionDIPExistingChargeDetail {
    Id: string;
    CaseSecurityId: string;
    ClientId: number;
    ClientAddressId: string;
    MortgageType: number;
    AccountNumber: string;
    ChargeTypeId: string;
    CommencementDate: number;
    Balance: number;
    MonthlyInstalment: number;
    RemainingTerm: number;
    IsMortgageArrears: boolean;
    Arrears: number;
    ArrearsMonths: number;
    ArrearsMonthsMissed: number;
    IsArrearsInLast12Months: number;
    ArrearsInLast12Months: number;
    HighestArrearsInLast12Months: number;
    IsLoanToBeRedeem: boolean;
    AmountToRedeem: number;
    Deleted: boolean = false;
    LegalBodyRoleMortgageLenderId: string;
    CaseContactLenderId: string;
    LegalBodyRoleRequest: object;
    CaseId: string;
    CompanyName: string;
    BankAccountId: string;
    BankAccount: string;
    Reference: string;
    FinalDayRedemption: string;
    RedemptionOptionId: string;
    IsBroker: boolean = false;
    MortgageTakenOutDate: number;
    LegalBodyLenderRoleId: string;
    LenderName: string;
    LegalBodyRoleId: string;

    /**
     * Creates an instance of TogetherAuctionDIPExistingChargeDetail.
     * @param {object} [params={}]
     * @memberof TogetherAuctionDIPExistingChargeDetail
     */
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CaseSecurityId: '00000000-0000-0000-0000-000000000000',
            ClientId: 0,
            ClientAddressId: '00000000-0000-0000-0000-000000000000',
            MortgageType: 0,
            AccountNumber: null,
            ChargeTypeId: null,
            CommencementDate: null,
            Balance: null,
            MonthlyInstalment: null,
            RemainingTerm: null,
            IsMortgageArrears: null,
            Arrears: null,
            ArrearsMonths: null,
            ArrearsMonthsMissed: null,
            IsArrearsInLast12Months: null,
            ArrearsInLast12Months: null,
            HighestArrearsInLast12Months: null,
            IsLoanToBeRedeem: null,
            AmountToRedeem: null,
            Deleted: false,
            LegalBodyRoleMortgageLenderId: null,
            CaseContactLenderId: null,
            LegalBodyRoleRequest: null,
            CaseId: '00000000-0000-0000-0000-000000000000',
            CompanyName: null,
            BankAccountId: null,
            BankAccount: null,
            Reference: null,
            FinalDayRedemption: null,
            RedemptionOptionId: null,
            IsBroker: false,
            MortgageTakenOutDate: null,
            LegalBodyLenderRoleId: null,
            LenderName: null,
            LegalBodyRoleId: '00000000-0000-0000-0000-000000000000'
        }));
    }
}


export class TogetherAuctionDIPCaseSecurityRequest {

    Id: string;
    CaseId: string;
    SecurityId: string;
    ChargeTypeId: string;
    IsPropertyOnMarket: boolean;
    IsRented: boolean;
    RentalIncome: number;
    IsAssuredShortholdAgreement: boolean;
    AuctionDate: string;
    AuctionNumber: string;
    AuctionHouse: string;
    IsPropertyOccupiedByOtherThanApplicant: boolean;
    IsEquitableCharge: boolean;
    IsMortgageLender: boolean;
    IsMortgageProtected: boolean;
    IsConsentRequired: boolean;
    IsConsentGranted: boolean;
    IsLeaseCopyReceived: boolean;
    IsResidencychecked: boolean;
    HousingAssociation: boolean;
    ShareHeld: string;
    HasSharedOwnership: boolean;
    MonthlyRent: number;
    CommercialPropertyStatus: string;
    Deleted: boolean;
    ValuationValidUntil: number;
    ValuerName: string;
    ConfidenceLevel: string;
    SecurityValuationDate: number;
    Sect125RefNumber: string;
    DiscountAmount: number;
    SecurityValuationPurchasePrice: number;
    DiscountPercentage: number;
    CouncilValuerName: string;
    IsTogetherResidential: boolean;
    PropertyCategoryId: string;
    IsPropertyOwnerOccupied: boolean;
    CouncilValuation: number;
    ValuerNameId: string;
    PreviousValuationAmountOMV: number;
    PreviousValuationAmountPMV: number;
    LegalBodyCouncilValuerRoleId: string;
    LegalBodyValuerRoleId: string;
    IsBuyToLetProperty: boolean;

    Address: TogetherAuctionDIPAddressParent = new TogetherAuctionDIPAddressParent();

    SecurityRequest: TogetherAuctionDIPSecurityRequest = new TogetherAuctionDIPSecurityRequest();

    CaseSecurityAttributeRequest: Array<TogetherAuctionDIPCaseSecurityAttributeRequest> = [
        new TogetherAuctionDIPCaseSecurityAttributeRequest()
    ];

    ValuationRequest: Array<TogetherAuctionDIPValuationRequest> = [
        new TogetherAuctionDIPValuationRequest()
    ];

    /**
     * Creates an instance of TogetherAuctionDIPSecurityDetails.
     * @param {any} params
     * @memberof TogetherAuctionDIPSecurityDetails
     */
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CaseId: '00000000-0000-0000-0000-000000000000',
            SecurityId: '00000000-0000-0000-0000-000000000000',
            ChargeTypeId: null,
            IsPropertyOnMarket: null,
            IsRented: null,
            RentalIncome: null,
            IsAssuredShortholdAgreement: false,
            AuctionDate: null,
            AuctionNumber: null,
            AuctionHouse: null,
            IsPropertyOccupiedByOtherThanApplicant: null,
            IsEquitableCharge: null,
            IsMortgageLender: null,
            IsMortgageProtected: null,
            IsConsentRequired: null,
            IsConsentGranted: null,
            IsLeaseCopyReceived: null,
            IsResidencychecked: null,
            HousingAssociation: null,
            ShareHeld: null,
            HasSharedOwnership: null,
            MonthlyRent: null,
            CommercialPropertyStatus: null,
            Deleted: false,
            ValuationValidUntil: null,
            ValuerName: null,
            ConfidenceLevel: null,
            SecurityValuationDate: null,
            Sect125RefNumber: null,
            DiscountAmount: null,
            SecurityValuationPurchasePrice: null,
            DiscountPercentage: null,
            CouncilValuerName: null,
            IsBroker: false,
            CompanyName: null,
            ComparableAddressId: null,
            IsTogetherResidential: false,
            PropertyCategoryId: null,
            IsPropertyOwnerOccupied: null,
            CouncilValuation: null,
            ValuerNameId: null,
            PreviousValuationAmountOMV: null,
            PreviousValuationAmountPMV: null,
            LegalBodyCouncilValuerRoleId: null,
            LegalBodyValuerRoleId: null,
            IsBuyToLetProperty: null
        }));
    }
}

/**
 * Security details.
 *
 * @export
 * @class TogetherAuctionDIPSecurityDetails
 */
export class TogetherAuctionDIPSecurityDetails {

    /**
     * Case security request.
     *
     * @type {TogetherAuctionDIPCaseSecurityRequest}
     * @memberof TogetherAuctionDIPSecurityDetails
     */
    caseSecurityRequest: TogetherAuctionDIPCaseSecurityRequest = new TogetherAuctionDIPCaseSecurityRequest();

    existingChargeDetailRequest: TogetherAuctionDIPExistingChargeDetail[] = [
        new TogetherAuctionDIPExistingChargeDetail({ MortgageType: 1 }),
        new TogetherAuctionDIPExistingChargeDetail({ MortgageType: 2 })
    ];

    hasFirstCharge() {
        return _.isNil(this.existingChargeDetailRequest[0]) == false;
    }

    hasSecondCharge() {
        return _.isNil(this.existingChargeDetailRequest[1]) == false;
    }

    initialiseFirstCharge() {
        this.existingChargeDetailRequest[0] = new TogetherAuctionDIPExistingChargeDetail({ MortgageType: 1 });
        this.existingChargeDetailRequest[0].CaseId = this.caseSecurityRequest.CaseId;
        this.existingChargeDetailRequest[0].CaseSecurityId = this.caseSecurityRequest.SecurityId;
    }

    initialiseSecondCharge() {
        this.existingChargeDetailRequest[1] = new TogetherAuctionDIPExistingChargeDetail({ MortgageType: 2 });
        this.existingChargeDetailRequest[1].CaseId = this.caseSecurityRequest.CaseId;
        this.existingChargeDetailRequest[1].CaseSecurityId = this.caseSecurityRequest.SecurityId;
    }

    set _firstChargeValue(newVal: number) {
        this.existingChargeDetailRequest[0].Balance = newVal;
    }

    set _firstChargeLender(newVal: string) {
        this.existingChargeDetailRequest[0].LenderName = newVal;
    }

    set _secondChargeValue(newVal: number) {
        this.existingChargeDetailRequest[1].Balance = newVal;
    }

    set _secondChargeLender(newVal: string) {
        this.existingChargeDetailRequest[1].LenderName = newVal;
    }

    get _firstChargeValue() {
        return _.isNil(this.existingChargeDetailRequest) == false && this.existingChargeDetailRequest.length > 0 ? this.existingChargeDetailRequest[0].Balance : null;
    }

    get _firstChargeLender() {
        return _.isNil(this.existingChargeDetailRequest) == false && this.existingChargeDetailRequest.length > 0 ? this.existingChargeDetailRequest[0].LenderName || '' : '';
    }

    get _secondChargeValue() {
        return _.isNil(this.existingChargeDetailRequest) == false && this.existingChargeDetailRequest.length > 1 ? this.existingChargeDetailRequest[1].Balance : null;
    }

    get _secondChargeLender() {
        return _.isNil(this.existingChargeDetailRequest) == false && this.existingChargeDetailRequest.length > 1 ? this.existingChargeDetailRequest[1].LenderName || '' : '';
    }

    get _purchaseDate() {
        return _.isArray(this.caseSecurityRequest.ValuationRequest) &&
            _.isNil(this.caseSecurityRequest.ValuationRequest[0].ValuationDate) ? null : new Date(this.caseSecurityRequest.ValuationRequest[0].ValuationDate);
    }

    set _purchaseDate(newVal: Date) {
        if (_.isArray(this.caseSecurityRequest.ValuationRequest)) {
            this.caseSecurityRequest.ValuationRequest[0].ValuationDate = _.isNil(newVal) ? null : newVal.toISOString();
        }
    }

    get _purchasePrice() {
        return _.isArray(this.caseSecurityRequest.ValuationRequest) ? this.caseSecurityRequest.ValuationRequest[0].OpenMarketValue : null;
    }

    set _purchasePrice(newVal: number) {
        if (_.isArray(this.caseSecurityRequest.ValuationRequest)) {
            this.caseSecurityRequest.ValuationRequest[0].PurchasePrice = newVal;
        }
    }

    get _propertyValue() {
        return _.isArray(this.caseSecurityRequest.ValuationRequest) && !_.isNil(this.caseSecurityRequest.ValuationRequest[1]) ? this.caseSecurityRequest.ValuationRequest[1].OpenMarketValue : null;
    }

    set _propertyValue(newVal: number) {
        if (_.isArray(this.caseSecurityRequest.ValuationRequest)) {
            this.caseSecurityRequest.ValuationRequest[0].EstimatedValue = newVal;
        }
    }

    /**
     * Creates an instance of TogetherAuctionDIPSecurityDetails.
     * @param {object} [params={}]
     * @memberof TogetherAuctionDIPSecurityDetails
     */
    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
        }));
    }
}

export class Product {
    AcceptanceFee: number;
    ExitFee: number;
    TitleInsuranceFee: number;
    AdminFee: number;
    TTFee: number;
    InitialRepayment: number;
    RevisedRepayment: number;
    InitialRate: number;
    RevisedRate: number;
    FinalPayment: number;
    TotalAmountToBeRepaid: number;
    PlanCode: string;
    PlanDescription: string;
    IsSelectedProduct: boolean;
}


export class TogetherAuctionDIPOutcome_DIP {
    CaseReference: number;
    ChecklistName: Array<string> = [];
    Country: string;
    County: string;
    District: string;
    HouseName: string;
    HouseNumber: string;
    InitialRepayment: number;
    IsDIPExpired: boolean = false;
    ISDipMaterialFactChanged: boolean = false;
    IsLatestDocument: boolean = false;
    LoanAmount: number;
    MonthlyNominalRate: number;
    NominalRate: number;
    Outcome: 0;
    Payment: string;
    PostTown: string;
    Postcode: string;
    Street1: string;
    Street2: string;
    SubmissionDate: string;
    TimeAtAddress: string;
    TypeId: number;
    Products: Array<Product> = [];

    constructor(param: object = {}) {
        Object.assign(this, _.defaults(param, {
            "CaseReference": 0,
            "NominalRate": 0,
            "MonthlyNominalRate": null,
            "Payment": null,
            "SubmissionDate": new Date().toISOString(),
            "HouseName": null,
            "HouseNumber": null,
            "Street1": null,
            "Street2": null,
            "PostTown": null,
            "County": null,
            "Postcode": null,
            "TimeAtAddress": null,
            "Country": null,
            "District": null,
            "InitialRepayment": 0,
            "TypeId": 0,
            "LoanAmount": 0,
            "Outcome": 0,
            "IsDIPExpired": false,
            "IsLatestDocument": false,
            "IsDipMaterialFactChanged": false
        }));
    }

}

export class TogetherAuctionDIPEquifaxAddress {
    County: string;

    District: string;

    ClientAddressId: string;

    HasPostCode: boolean;

    HouseName: string;

    HouseNo: string;

    PostTown: string;

    Postcode: string;

    PtcAbsCode: string;
    Street1: string;
    Street2: string;

    Surname: string;


    /**
     * Constructs full address.
     *
     * @readonly
     * @memberof TogetherAuctionDIPEquifaxAddress
     */
    get fullAddress() {
        return `${this.HouseName} ${this.HouseNo} ${this.Street1} ${this.Street2}, ${this.PostTown}, ${this.Postcode}`;
    }

    constructor(params) {
        Object.assign(this, _.defaults(params, {}));
    }

}

export class TogetherAuctionDIPSolicitorAddressRequest extends TogetherBaseAddress {
    Id: number = 0;
    Country: string;
    Street: string;

    constructor(params: object = {}) {
        super();
        Object.assign(this, _.defaults(params, {
            Id: 0,
            Country: null
        }));
    }
}

export class TogetherAuctionDIPSolicitorRequest {
    Id: string;
    CaseId: string;
    CompanyName: string;
    ContactName: string;
    PhoneNumber: string;
    Email: string;
    FaxNumber: string;
    ReferenceNumber: string;
    ContactTypeId: number = 1;
    LegalBodyRoleId: string;
    AddressId: number = 0;
    ComplaintName: string;
    ComplaintDepartment: string;
    ComplaintTelephoneNumber: string;
    ComplaintWebsite: string;
    Note: string;
    Mobile: string;
    FCAStatusId: string;
    NumberOfPartners: number;
    CommissionAmount: number;
    IsAdvisorCaseContact: boolean = false;
    IsComplaintCaseContact: boolean = false;
    CaseContactAddressRequest: TogetherAuctionDIPSolicitorAddressRequest;
    CCLNumber: string;
    PrincipalFSANumber: string;
    LenderId: string = '00000000-0000-0000-0000-000000000000';
    IsDisbursedTo: boolean = false;
    IntermediaryId: string;
    MatchedLegalBodyName: string;
    Deleted: boolean = false;


    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            'saveContactRequest': [],
            'CaseContactAddressRequest': new TogetherAuctionDIPSolicitorAddressRequest(),
            'Id': '00000000-0000-0000-0000-000000000000',
            'CaseId': '00000000-0000-0000-0000-000000000000',
            'CompanyName': '',
            'ContactName': '',
            'PhoneNumber': '',
            'Email': '',
            'FaxNumber': null,
            'ReferenceNumber': null,
            'ContactTypeId': 1,
            'LegalBodyRoleId': null,
            'AddressId': 0,
            'ComplaintName': null,
            'ComplaintDepartment': null,
            'ComplaintTelephoneNumber': null,
            'ComplaintWebsite': null,
            'Note': null,
            'Mobile': null,
            'FCAStatusId': null,
            'PrincipalFSANumber': null,
            'MatchedLegalBodyName': null,
            'CCLNumber': null,
            'IntermediaryId': null,
            'NumberOfPartners': null,
            'CommissionAmount': null,
            'IsAdvisorCaseContact': false,
            'IsComplaintCaseContact': false,
        }));
    }

}

export class TogetherAuctionDIPQuoteResult {
    CaseReference: string;
    Outcome: boolean;

    constructor(params) {
        if (typeof params == 'boolean') {
            this.Outcome = params;
        } else {
            Object.assign(this, _.defaults(params, {
                CaseReference: '0',
                Outcome: null
            }));
        }
    }
}

export class TogetherAuctionDIPStageRequest {
    CaseId: string;
    StepId: string;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {}));
    }
}

export class TogetherAuctionDIPApplicantCompanyDetails {
    Id: string;
    CaseId: string;
    IsCompanyApplication: boolean;
    IsCompanyGuaranteeProvided: boolean;
    IsDebentureProvided: boolean;
    IsGuarantorRequired: boolean;
    BrokerReference: string;
    lenderId: number;
    LegalBodyRoleapplicantId: string;
    LegalBodyRoleId: string;
    LegalBodyRoleTypeId: number;
    LegalBodyId: string;
    LegalBodyTypeId: string;
    CompanyName: string;
    IsJHGroupCompany: boolean;
    ApplicantSequence: number;
    RegistrationNumber: string;
    LegalBodyRoleAddresses: Array<TogetherAuctionDIPApplicantDetailsLegalBodyRoleAddresses> = [];
    IsBroker: boolean;
    IsSignatureCheckedAndAccepted: boolean;
    MarketingMarker: string;
    CompanyTypeId: string;
    AnnualDebitTurnover: number;
    RegistrationDate: string;

    constructor(params: object = {}) {
        Object.assign(this, _.defaults(params, {
            Id: '00000000-0000-0000-0000-000000000000',
            CaseId: '00000000-0000-0000-0000-000000000000',
            IsCompanyApplication: true,
            IsCompanyGuaranteeProvided: null,
            IsDebentureProvided: null,
            IsGuarantorRequired: null,
            IsPersonalGuarantor: null,
            BrokerReference: null,
            lenderId: 428,
            LegalBodyRoleApplicantId: '00000000-0000-0000-0000-000000000000',
            LegalBodyRoleId: '00000000-0000-0000-0000-000000000000',
            LegalBodyRoleTypeId: 0,
            LegalBodyId: '00000000-0000-0000-0000-000000000000',
            LegalBodyTypeId: 0,
            CompanyName: null,
            IsJHGroupCompany: null,
            ApplicantSequence: 1,
            RegistrationNumber: null,
            IsBroker: false,
            IsSignatureCheckedAndAccepted: null,
            MarketingMarker: null,
            CompanyTypeId: null,
            AnnualDebitTurnover: null,
            RegistrationDate: null
        }));
    }
}
